import { useEffect, useState } from 'react'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import HistoryIcon from '@mui/icons-material/HistoryOutlined'
import {
  dateHeureLisible,
  format,
  money,
  type CurrencyCode,
  type TransactionRevision,
} from '@iobiz/shared'
import { api } from '../../api/client.js'

/**
 * Ce que le document a été avant d'être corrigé.
 *
 * ⭐ Sans cet écran, l'archive ne servirait à rien. C'est elle qui rend
 * l'amendement d'ADR-08 défendable : on a levé l'interdiction de modifier une
 * facture validée, à la condition que l'état d'avant reste consultable par qui
 * tient la boutique — pas seulement par qui sait lire une table SQL.
 *
 * ⚠️ Chargé depuis le SERVEUR, pas de la réplique. Les révisions ne descendent
 * pas sur les appareils : elles alourdiraient chaque appareil pour un écran
 * qu'on ouvre trois fois par an. Hors ligne, la section reste donc absente —
 * ce qui est préférable à une section vide qui laisserait croire qu'aucune
 * modification n'a eu lieu.
 */
export function HistoriqueRevisions({
  storeId,
  venteId,
  devise,
}: {
  storeId: string
  venteId: string
  devise: CurrencyCode
}): JSX.Element | null {
  const [revisions, setRevisions] = useState<TransactionRevision[] | null>(null)

  useEffect(() => {
    let vivant = true
    api
      .get<{ data: TransactionRevision[] }>(`/stores/${storeId}/transactions/${venteId}/revisions`)
      .then((r) => {
        if (vivant) setRevisions(r.data)
      })
      // Silencieux À DESSEIN : hors ligne, l'appel échoue et la section
      // disparaît. Une erreur rouge sur un historique facultatif ferait
      // craindre pour le document lui-même.
      .catch(() => {})
    return () => {
      vivant = false
    }
  }, [storeId, venteId])

  if (!revisions || revisions.length === 0) return null

  const prix = (v: number): string => format(money(v, devise), 'fr-FR')

  return (
    <Accordion variant="outlined" disableGutters sx={{ mt: 4 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HistoryIcon fontSize="small" />
          <Typography sx={{ fontWeight: 600 }}>Historique des modifications</Typography>
          <Chip label={revisions.length} size="small" />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {revisions.map((r) => (
          <Box key={r.id} sx={{ mb: 4 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {dateHeureLisible(r.changedAt)}
              {r.changedByName ? ` · ${r.changedByName}` : ''}
            </Typography>
            {r.reason && (
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                Motif : {r.reason}
              </Typography>
            )}
            {/* ⭐ L'état d'AVANT, pas un écart. On montre ce qui était facturé,
                ce qui est la question posée quand un client conteste. */}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Version {r.version} — total {prix(r.snapshot.totalAmount)}
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableBody>
                {r.snapshot.lines.map((l) => (
                  <TableRow key={l.id}>
                    <TableCell>{l.label}</TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {l.quantity} × {prix(l.unitPrice)}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {prix(l.total)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        ))}
      </AccordionDetails>
    </Accordion>
  )
}
